import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";

const AdminAnalytics = () => {
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [users, setUsers] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({
    mostViewedInvestors: [],
    mostActiveInvestors: [],
    chatsStarted: [],
    dailyActiveUsers: [],
    signupTrends: [],
  });

  const navigate = useNavigate();

  const fetchUsers = async () => {
    const snapshot = await getDocs(query(collection(db, "users"), orderBy("email")));
    const userList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setUsers(userList);
    calculateAnalytics(userList);
  };

  const calculateAnalytics = (userList) => {
    const investors = userList.filter((u) => u.role?.toLowerCase() === "investor");
    const founders = userList.filter((u) => u.role?.toLowerCase() === "founder");

    const mostViewedInvestors = [...investors]
      .sort((a, b) => (b.profileViews || 0) - (a.profileViews || 0))
      .slice(0, 5);

    const mostActiveInvestors = [...investors]
      .sort((a, b) => (b.repliesCount || 0) - (a.repliesCount || 0))
      .slice(0, 5);

    const chatsStartedMap = {};
    founders.forEach((f) => {
      (f.chatsStarted || []).forEach((chat) => {
        const date = new Date(chat.timestamp.seconds * 1000).toISOString().split("T")[0];
        chatsStartedMap[date] = (chatsStartedMap[date] || 0) + 1;
      });
    });
    const chatsStarted = Object.entries(chatsStartedMap).map(([date, count]) => ({ date, count }));

    const now = new Date();
    const dailyActiveUsers = userList
      .filter((u) => u.lastActive && now - u.lastActive.toDate() <= 24 * 60 * 60 * 1000)
      .map((u) => ({ email: u.email }));

    const signupMap = {};
    userList.forEach((u) => {
      if (!u.createdAt) return;
      const date = new Date(u.createdAt.seconds * 1000).toISOString().split("T")[0];
      signupMap[date] = (signupMap[date] || 0) + 1;
    });
    const signupTrends = Object.entries(signupMap).map(([date, count]) => ({ date, count }));

    setAnalyticsData({
      mostViewedInvestors,
      mostActiveInvestors,
      chatsStarted,
      dailyActiveUsers,
      signupTrends,
    });
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        setLoadingAuth(false);
        navigate("/login");
        return;
      }
      fetchUsers().finally(() => setLoadingAuth(false));
    });
    return () => unsubscribeAuth();
  }, [navigate]);

  if (loadingAuth) {
    return <div className="flex justify-center items-center h-screen">⏳ Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 relative">
      <button
        onClick={handleLogout}
        className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
      >
        🚪 Logout
      </button>

      {/* Royal Breadcrumb */}
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link
          to="/admin-dashboard"
          className="text-blue-600 hover:text-purple-700 hover:underline transition-colors duration-200"
        >
          🛡️ Dashboard
        </Link>
        <span className="mx-1">›</span>
        <span>📊 Analytics</span>
      </div>

      <h1 className="text-3xl font-bold mb-6">📊 Admin Analytics</h1>

      {/* Most Viewed Investors */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">👀 Most Viewed Investors</h2>
        {analyticsData.mostViewedInvestors.length === 0 ? (
          <p className="text-gray-500">No data available</p>
        ) : (
          <ul className="list-disc list-inside">
            {analyticsData.mostViewedInvestors.map((inv) => (
              <li key={inv.id}>
                {inv.fullName || inv.email} - {inv.profileViews || 0} views
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Most Active Investors */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">💬 Investors with Most Replies</h2>
        {analyticsData.mostActiveInvestors.length === 0 ? (
          <p className="text-gray-500">No data available</p>
        ) : (
          <ul className="list-disc list-inside">
            {analyticsData.mostActiveInvestors.map((inv) => (
              <li key={inv.id}>
                {inv.fullName || inv.email} - {inv.repliesCount || 0} replies
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Chats Started Line Chart */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">🚀 Chats Started (by Founders)</h2>
        {analyticsData.chatsStarted.length === 0 ? (
          <p className="text-gray-500">No chats started yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={analyticsData.chatsStarted}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#8884d8" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Signup Trends Bar Chart */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">📅 Signup Trends</h2>
        {analyticsData.signupTrends.length === 0 ? (
          <p className="text-gray-500">No signups yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={analyticsData.signupTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Daily Active Users */}
      <div className="bg-white shadow-md rounded-2xl p-6 mb-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">🟢 Daily Active Users (Last 24h)</h2>
        {analyticsData.dailyActiveUsers.length === 0 ? (
          <p className="text-gray-500">No active users in last 24 hours</p>
        ) : (
          <ul className="list-disc list-inside max-h-40 overflow-y-auto">
            {analyticsData.dailyActiveUsers.map((u) => (
              <li key={u.email}>{u.email}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
